import { useEffect, useRef, useState } from 'react';

const carriesImages = (event: DragEvent) => Array.from(event.dataTransfer?.items ?? []).some(item => item.kind === 'file' && item.type.startsWith('image/'));

/**
 * Whether image files are being dragged anywhere over the page right now.
 *
 * Counted rather than read off each `dragleave`'s `relatedTarget`: every element the drag crosses
 * fires an enter and a leave, and Safari reports a null `relatedTarget` on all of them, so only the
 * balance of the two says whether the drag is still over the page. Listened for on the window, in
 * the capture phase, so a drop zone that stops propagation still ends the drag here.
 */
export const useImageDragInProgress = () => {
  const [imageDragInProgress, setImageDragInProgress] = useState(false);
  const depth = useRef(0);

  useEffect(() => {
    const enter = (event: DragEvent) => {
      if (!carriesImages(event)) return;
      depth.current += 1;
      setImageDragInProgress(true);
    };
    const leave = () => {
      depth.current = Math.max(0, depth.current - 1);
      if (!depth.current) setImageDragInProgress(false);
    };
    const end = () => {
      depth.current = 0;
      setImageDragInProgress(false);
    };
    /**
     * A drop ends the drag on a later task, not here. A capture listener on the window runs before
     * React's own, and the browser flushes React's sync update at the microtask checkpoint between the
     * two: the drop zones would unmount, and React would then find no mounted target for the drop, so
     * the zone's own `onDrop` would never run.
     */
    const endAfterDrop = () => setTimeout(end);
    window.addEventListener('dragenter', enter, true);
    window.addEventListener('dragleave', leave, true);
    window.addEventListener('drop', endAfterDrop, true);
    window.addEventListener('dragend', end, true);
    return () => {
      window.removeEventListener('dragenter', enter, true);
      window.removeEventListener('dragleave', leave, true);
      window.removeEventListener('drop', endAfterDrop, true);
      window.removeEventListener('dragend', end, true);
    };
  }, []);

  return imageDragInProgress;
};
